import React, { useEffect, useRef, useState } from 'react';
import { getTopMatchKombis } from '../../../api/api';
import { TopMatchKombiItem } from '../../../api/types';
import { useTranslation } from '../../../i18n';

function formatOdds(val: string | number | undefined): string {
  if (val === undefined || val === null) return '-';
  const n = parseFloat(String(val));
  if (isNaN(n) || n < 1.01) return '-';
  return n.toFixed(2).replace('.', ',');
}

interface TopMatchKombisProps {
  /** Toggles the card in the bet slip as one provider-priced CustomBet group. */
  onAddMatchKombi?: (item: TopMatchKombiItem) => void;
  /** Opens the event detail with the Bet Builder+ tab active. */
  onOpenBuilder?: (item: TopMatchKombiItem) => void;
  /** Whether this card's combo is currently in the slip (for selected styling). */
  isSelected?: (item: TopMatchKombiItem) => boolean;
}

export const TopMatchKombis: React.FC<TopMatchKombisProps> = ({ onAddMatchKombi, onOpenBuilder, isSelected }) => {
  const { locale, t } = useTranslation();
  const [items, setItems] = useState<TopMatchKombiItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let active = true;
    getTopMatchKombis({ view: 'home', limit: 6, locale })
      .then((res) => { if (active) setItems(res.items || []); })
      .catch(() => { if (active) setItems([]); })
      .finally(() => { if (active) setLoaded(true); });
    return () => { active = false; };
  }, [locale]);

  const scroll = (dir: 'left' | 'right') => {
    const el = sliderRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === 'left' ? -320 : 320, behavior: 'smooth' });
  };

  // Only render once we have data — keep the home page clean when unavailable.
  if (!loaded || items.length === 0) return null;

  return (
    <div className="mb-4">
      <div className="flex items-end justify-end mb-3 px-2">
        <button className="text-[15px] font-bold text-gray-500 hover:text-gray-900 transition flex items-center gap-1">
          {t('section.showAll')}
          <span className="text-lg leading-none">›</span>
        </button>
      </div>

      <div className="relative w-full group/mkslider px-2">
        <button
          onClick={() => scroll('left')}
          className="absolute left-[-10px] top-1/2 -translate-y-1/2 z-20 bg-white text-gray-700 rounded-full w-10 h-10 flex items-center justify-center shadow-md border border-gray-100 opacity-0 group-hover/mkslider:opacity-100 transition-opacity hover:bg-gray-50"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </button>

        <div ref={sliderRef} className="flex gap-3 overflow-x-auto scrollbar-hide snap-x pb-2">
          {items.map((item) => (
            <div
              key={item.combination_id}
              className="snap-start flex-shrink-0 w-[320px] bg-white border border-gray-200 rounded-lg shadow-sm flex flex-col"
            >
              {/* Match header */}
              <div className="relative -mx-[1px] -mt-[1px] h-[40px] flex items-center px-4 rounded-t-lg overflow-hidden border-b border-gray-100">
                {/* Slanted Backgrounds (Mobile) */}
                <div className="absolute top-0 left-0 h-full w-[80%] rounded-tl-lg md:hidden" style={{ backgroundImage: 'linear-gradient(rgb(156, 9, 41) 0%, rgb(57, 7, 18) 100%)', clipPath: 'polygon(0 0, 100% 0, calc(100% - 15px) 100%, 0 100%)' }} />
                <div className="absolute top-0 left-[calc(80%+3px)] h-full w-[4px] transform -skew-x-[22deg] origin-bottom md:hidden" style={{ backgroundImage: 'linear-gradient(rgb(156, 9, 41) 0%, rgb(57, 7, 18) 100%)' }} />

                {/* Slanted Backgrounds (Desktop) */}
                <div className="absolute top-0 left-0 h-full w-[80%] rounded-tl-lg hidden md:block" style={{ backgroundImage: 'var(--bg-brand-gradient-vertical)', clipPath: 'polygon(0 0, 100% 0, calc(100% - 15px) 100%, 0 100%)' }} />
                <div className="absolute top-0 left-[calc(80%+3px)] h-full w-[4px] transform -skew-x-[22deg] origin-bottom hidden md:block" style={{ backgroundImage: 'var(--bg-brand-gradient-vertical)' }} />

                <span className="text-[14px] font-bold text-white relative z-10 leading-tight truncate">{item.event_name}</span>
              </div>

              {/* Selections */}
              <div className="flex-1 flex flex-col gap-2 px-4 py-3">
                {item.selections.map((sel, i) => (
                  <div key={`${sel.market_id}-${sel.specifiers}-${sel.outcome_id}-${i}`} className="flex items-start gap-2">
                    <span className="mt-[3px] w-[10px] h-[10px] rounded-full border-2 border-gray-300 flex-shrink-0" />
                    <span className="text-[14px] leading-snug text-gray-700 truncate">
                      <span className="text-gray-500">{sel.market_name}: </span>
                      <span className="font-bold text-gray-900">{sel.outcome_name}</span>
                    </span>
                  </div>
                ))}
              </div>

              {/* Footer: open Bet Builder+ (left) · add-to-slip odds box (right) */}
              <div className="flex items-stretch gap-2 px-3 pb-3">
                <button
                  onClick={() => onOpenBuilder?.(item)}
                  disabled={!onOpenBuilder}
                  className="flex-1 border border-gray-300 rounded-md py-2 text-[14px] font-bold text-gray-800 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('section.createMkPlus')}
                </button>
                {(() => {
                  const selected = isSelected?.(item) || false;
                  return (
                    <button
                      onClick={() => onAddMatchKombi?.(item)}
                      disabled={!onAddMatchKombi}
                      className={`min-w-[80px] rounded-md flex items-center justify-center px-3 text-[15px] font-black border transition disabled:opacity-50 disabled:cursor-not-allowed ${
                        selected
                          ? 'bg-[#333] border-[#333] text-white dark:bg-[#D41534] dark:border-[#D41534]'
                          : 'bg-[#f0f2f5] border-[#c0c5cc] text-gray-900 hover:bg-[#e3e6eb] dark:bg-[#252525] dark:border-[#333] dark:text-white dark:hover:bg-[#333]'
                      }`}
                    >
                      {formatOdds(item.combined_odds)}
                    </button>
                  );
                })()}
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={() => scroll('right')}
          className="absolute right-[-10px] top-1/2 -translate-y-1/2 z-20 bg-white text-gray-700 rounded-full w-10 h-10 flex items-center justify-center shadow-md border border-gray-100 opacity-0 group-hover/mkslider:opacity-100 transition-opacity hover:bg-gray-50"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  );
};
